import { z } from 'zod';
import { PhoneNumberSchema } from '../common/phone-number.schema';

// Legal form of the firm. UPPER_CASE union codes per the domain-constants
// convention; the German display labels (GmbH, UG (haftungsbeschränkt), …) live
// only in `@immo/i18n` and are mapped from these codes on the client. The list
// is the curated set from the IMM-256 spec — free text is rejected.
export const LegalFormSchema = z.enum([
  'GMBH',
  'UG',
  'GMBH_CO_KG',
  'EINZELUNTERNEHMEN',
  'EK',
  'GBR',
  'PARTG_MBB',
  'OHG',
  'KG',
  'UG_CO_KG',
  'AG',
]);

export type LegalForm = z.infer<typeof LegalFormSchema>;

export const LEGAL_FORMS = LegalFormSchema.options;

const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

// Optional free-text field: an empty/whitespace submission means "not set"
// (→ null), a present value is trimmed and length-checked. `.default(null)`
// lets the key be omitted entirely.
const optionalText = (max: number) =>
  z.preprocess(emptyToNull, z.string().trim().min(1).max(max).nullable()).default(null);

const optionalPhone = z.preprocess(emptyToNull, PhoneNumberSchema.nullable()).default(null);

const optionalEmail = z.preprocess(emptyToNull, z.email().nullable()).default(null);

const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http(s) URL' });

// https only: the landing page links to it from a consumer-facing page (PRD Fields: „https URL, 0–300 chars").
const HttpsUrlSchema = z
  .url()
  .max(300)
  .refine((value) => /^https:\/\//i.test(value), { message: 'must be an https URL' });

const optionalHttpUrl = z.preprocess(emptyToNull, HttpUrlSchema.nullable()).default(null);

// German VAT identification number: `DE` followed by exactly 9 digits.
const VatIdSchema = z.string().regex(/^DE[0-9]{9}$/);

const optionalVatId = z.preprocess(emptyToNull, VatIdSchema.nullable()).default(null);

// The company address reuses the shared `identity.addresses` structure (the
// same the Standort/workspace address uses): all parts are required together
// when an address is given, with optional geocoding. The address as a whole is
// optional — `null` means no address maintained yet.
const CompanyAddressSchema = z.object({
  street: z.string().trim().min(1).max(100),
  houseNumber: z.string().trim().min(1).max(20),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(1).max(80),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});

export type CompanyAddress = z.infer<typeof CompanyAddressSchema>;

const CompanyAddressResponseSchema = z.object({
  street: z.string(),
  houseNumber: z.string(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

export const CompanyResponseSchema = z.object({
  workspaceId: z.uuid(),
  companyName: z.string().nullable(),
  legalForm: LegalFormSchema.nullable(),
  address: CompanyAddressResponseSchema.nullable(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  socialLinks: z.array(z.string()),
  // Legal-notice block (IMM-284).
  managingDirectors: z.string().nullable(),
  commercialRegisterCourt: z.string().nullable(),
  commercialRegisterNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  supervisoryAuthority: z.string().nullable(),
  professionalIndemnityInsurance: z.string().nullable(),
  // §34c GewO Erlaubnisbehörde + §18 Abs. 2 MStV Inhaltlich Verantwortliche (IMM-255.4).
  tradePermitAuthority: z.string().nullable(),
  contentResponsiblePerson: z.string().nullable(),
  // Datenschutzerklärung link for the Widerruf landing page and buyer e-mails (IMM-1065.1).
  privacyPolicyUrl: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});

export type CompanyResponse = z.infer<typeof CompanyResponseSchema>;

export const UpdateCompanyBodySchema = z
  .object({
    companyName: optionalText(120),
    legalForm: z.preprocess(emptyToNull, LegalFormSchema.nullable()).default(null),
    address: CompanyAddressSchema.nullable().default(null),
    phone: optionalPhone,
    fax: optionalPhone,
    email: optionalEmail,
    website: optionalHttpUrl,
    socialLinks: z
      .array(HttpUrlSchema.max(300))
      .refine((links) => new Set(links).size === links.length, { message: 'must not contain duplicate links' })
      .default([]),
    managingDirectors: optionalText(240),
    commercialRegisterCourt: optionalText(120),
    commercialRegisterNumber: optionalText(40),
    vatId: optionalVatId,
    supervisoryAuthority: optionalText(200),
    professionalIndemnityInsurance: optionalText(200),
    // NOT `.default(null)`: an absent key keeps the stored value. These two are written by company
    // onboarding and the partner approval, not by the settings form (IMM-1609).
    tradePermitAuthority: z.preprocess(emptyToNull, z.string().trim().min(1).max(200).nullable()).optional(),
    contentResponsiblePerson: z.preprocess(emptyToNull, z.string().trim().min(1).max(200).nullable()).optional(),
    // NOT `.default(null)`: an absent key keeps the stored value.
    privacyPolicyUrl: z.preprocess(emptyToNull, HttpsUrlSchema.nullable()).optional(),
  })
  .strict();

export type UpdateCompanyBody = z.infer<typeof UpdateCompanyBodySchema>;

export const CompanyPathParamsSchema = z.object({ workspaceId: z.uuid() });
